import React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from '@reach/router'
import { useStaticQuery, graphql } from 'gatsby'

interface Props {
	pageName?: string
}

const SEO = ({ pageName }: Props) => {
	const { pathname } = useLocation()
	const { site, allContentfulPageSeo }: { site: SiteQuery; allContentfulPageSeo: SEOQuery } = useStaticQuery(query)
	const { defaultTitle, titleTemplate, defaultDescription, defaultKeywords, siteUrl, defaultImage, twitterHandle } = site.siteMetadata
	const page = allContentfulPageSeo.edges.filter((page) => page.node.pageName === pageName)[0].node
	const seo = {
		title: page.title ? page.title : defaultTitle,
		description: page.description ? page.description : defaultDescription,
		keywords: page.keywords ? page.keywords.toString() : defaultKeywords,
		image: page.image ? page.image.file.url : `${siteUrl}${defaultImage}`,
		url: `${siteUrl}${pathname}`,
		schema: page.schema && JSON.parse(page.schema.internal.content)
	}

	return (
		<Helmet title={seo.title} titleTemplate={titleTemplate}>
			<title>{seo.title}</title>
			<meta name="description" content={seo.description} />
			<meta name="keywords" content={seo.keywords} />
			<meta name="image" content={seo.image} />
			{seo.url && <meta property="og:url" content={seo.url} />}
			{seo.title && <meta property="og:title" content={seo.title} />}
			{seo.description && <meta property="og:description" content={seo.description} />}
			{seo.image && <meta property="og:image" content={seo.image} />}
			<meta name="twitter:card" content="summary_large_image" />
			{twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}
			{seo.title && <meta name="twitter:title" content={seo.title} />}
			{seo.description && <meta name="twitter:description" content={seo.description} />}
			{seo.image && <meta name="twitter:image" content={seo.image} />}
			{seo.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
		</Helmet>
	)
}
export default SEO

interface SiteQuery {
	siteMetadata: {
		defaultTitle: string
		titleTemplate: string
		defaultDescription: string
		defaultKeywords: string
		siteUrl: string
		defaultImage: string
		twitterHandle: string
	}
}

interface SEOQueryNode {
	node: {
		pageName: string
		title: string
		description: string
		keywords: string[]
		schema: { internal: { content: string } }
		image: { file: { url: string } }
	}
}

interface SEOQuery {
	edges: SEOQueryNode[]
}

const query = graphql`
	query SEO {
		site {
			siteMetadata {
				defaultTitle: title
				titleTemplate
				defaultDescription: description
				defaultKeywords: keywords
				siteUrl
				defaultImage: image
				twitterHandle
			}
		}
		allContentfulPageSeo {
			edges {
				node {
					pageName
					title
					description
					keywords
					schema {
						internal {
							content
						}
					}
					image {
						file {
							url
						}
					}
				}
			}
		}
	}
`
